import { createVuetify } from "vuetify";
import * as components from "vuetify/components";
import * as directives from "vuetify/directives";
import { VTimePicker } from "vuetify/labs/VTimePicker";
import "vuetify/styles";

// Brand anchors. Vuetify theme values must be literal hex, so these cannot be
// imported from scss/_tokens.scss; they mirror its $color-dactyl-* block.
// Values are owned by the design system (design-system/COLOR.md).
const blue = "#347b99";
const orange = "#f09d37";
const darkOrange = "#f58634";
const turquoise = "#39c9bb";
const white = "#FAFAFA";
const trueWhite = "#ffffff";
const grey = "#f5f5f5";
const red = "#d32f2f";
const fgOnColor = "#ffffff"; // text/icons on a saturated fill

// REV 2.1.1 workflow buckets - seven states over four hues. Hue answers "does
// this need a human, and is anything wrong?", not "which stage is this?"; stage
// is carried by column position plus a mandatory glyph.
//
// Change the bucket, not the states. Components must name the STATE
// ("state-in-qc"), never the bucket, so a future re-bucketing is a change to
// these consts and no call-site sweep. This mirrors the aliasing that
// scss/_tokens.scss expresses with real SCSS variables.
const stateQueuedInk = "#5f6368";
const stateQueued = "#8b949c";
const stateQueuedTint = "#f3f4f5";
const onQueued = "#212121"; // 5.04:1 on #8b949c - queued fills may NOT use white (3.02:1)
const stateActiveInk = "#8f5200";
const stateActive = "#f09d37"; // FILL ONLY, 2.19:1
const stateActiveEdge = "#b3671a";
const stateActiveTint = "#fef5eb";
const stateReworkInk = "#a84300"; // rust - always with mdi-arrow-u-left-top
const stateRework = "#c2410c";
const stateReworkTint = "#f9ece7";
const stateCompleteInk = "#1b5e20";
const stateComplete = "#2e7d32";
const stateCompleteTint = "#eaf2eb";

export default createVuetify({
	components: { ...components, VTimePicker },
	defaults: {
		VTextField: {
			variant: "underlined",
			color: "secondary",
		},
		VCombobox: {
			bgColor: "trueWhite",
			color: "secondary",
		},
		VAutocomplete: {
			bgColor: "trueWhite",
			color: "secondary",
		},
		VSelect: {
			bgColor: "trueWhite",
			color: "secondary",
		},
	},
	directives,
	theme: {
		// Theme name stays "light" and defaultTheme stays "light": the
		// v-theme--light class name and five theme="dark" props depend on it.
		// Do not rename to dactylLight.
		defaultTheme: "light",
		themes: {
			light: {
				colors: {
					// --- Vuetify roles ------------------------------------
					primary: blue,
					"on-primary": fgOnColor,
					// The opaque wash for the `primary` role, and its
					// `secondary` twin below. Added by DCTL-2441, which
					// retired 31 `rgba(var(--v-theme-<key>), <alpha>)` fills
					// - 9 primary, 22 secondary - spread over eleven
					// different opacities on one key.
					//
					// Both values are the base key at 0.15 over #ffffff,
					// which is exactly what the heaviest sites were already
					// compositing to, so the dominant call sites are
					// pixel-identical. The rest FLATTEN onto it: 0.04 and
					// 0.25 both land here. That is the ruling, not a
					// side effect - an alpha resolves against whatever is
					// painted behind it and no token knows what that is.
					//
					// Declared as theme keys rather than written as hexes
					// for the reason surface-tint states below: without the
					// key Vuetify emits no `bg-*` utility, and scoped CSS
					// cannot say `rgb(var(--v-theme-primary-tint))` either.
					//
					// on-primary-tint / on-secondary-tint are PINNED. Left
					// undeclared Vuetify derives them, and getForeground
					// returns #000 for both - off the text ramp entirely,
					// whose darkest step is fg-max #212121. #424242 is fg-1,
					// reading 8.30:1 on #e1ebf0 and 8.96:1 on #fdf0e1. This
					// changes no pixel today: all 31 sites are scoped-CSS
					// `background-color`, so none consumes an on-colour.
					// It is forward cover, the same kind on-warning and
					// on-success shipped as, and it is what lets
					// lint-on-color guard the pair - text-white on
					// #fdf0e1 is 1.12:1.
					//
					// NOT the role ink in either case, and it stays that
					// way now that primary-ink exists below. A `.bg-*-tint`
					// default should be the SAFEST ink, not the most
					// expressive one: fg-1 clears 8:1 on both tints, while
					// blue on primary-tint is 5.31:1 - legible, but a choice
					// a call site should have to make out loud rather than
					// inherit. secondary-ink #8f5200 would likewise do
					// (5.55:1 on its tint) and is likewise not the default.
					"primary-tint": "#e1ebf0",
					"on-primary-tint": "#424242",
					// `primary` as TEXT. The base is 4.73:1 on white but only
					// 3.91:1 on primary-tint, which DCTL-2441 created and
					// `.mode-bar--move` paints blue text on - so the tint is
					// what forced this key.
					//
					// A NEW NAME ON AN EXISTING VALUE. #2c657d already appears
					// as provenance-a-ink, valence-info-ink and axis-x, because
					// it is the readable dark form of #347b99 and every axis
					// needing a dark blue landed on it. The coincidence is BY
					// CONSTRUCTION; the names stay apart for the reason
					// secondary-tint stays apart from provenance-b-tint, since
					// chrome is not authorship and either can be re-ruled alone.
					//
					// NOT in lint-on-color's GUARDED_KEYS: that guard is about a
					// `bg-<key>` carrying text-white, and an ink is never a
					// background.
					"primary-ink": "#2c657d", // 6.43 white / 6.16 #fafafa / 5.31 own tint
					secondary: orange,
					// NOT fgOnColor, and not derivable. Left undeclared,
					// Vuetify derives black here - but `white` is also a
					// declared theme key, so every orange CTA carried an
					// explicit text-white and rendered #FAFAFA on #f09d37 =
					// 2.10:1, a shipping WCAG 1.4.3 failure. 6.49:1.
					// DCTL-2282 declared this key and deleted the 108
					// text-white overrides that were hiding it; the two halves
					// only work together, because Vuetify emits every bg- rule
					// before every text- rule at equal specificity
					// (composables/theme.js, `lines.push(...bgLines,
					// ...fgLines)`), so .text-white wins on source order.
					"on-secondary": "#3d2600",
					"secondary-darken-1": darkOrange,
					// The two AA-safe FOREGROUND forms of `secondary`, added by
					// DCTL-2284. `on-secondary` above only reaches orange
					// *fills*; every foreground use of `secondary` reads the base
					// key and renders 2.19:1. These are the migration targets
					// MIGRATION.md names for that row ("#f09d37 as text or
					// border").
					//
					// Spelled `secondary-*` and not `orange-*` on purpose: every
					// -ink / -edge key in this file hangs off a base key declared
					// beside it (provenance-b, state-progress, valence-attention),
					// and there is no `orange` theme key for an `orange-ink` to
					// hang off. The scss layer spells the same two hexes
					// $color-dactyl-orange-ink / -edge, which is the same
					// two-layer split `secondary` <-> --dactyl-orange already has.
					// Do not add a third spelling.
					//
					// Literal hex rather than the stateActiveInk / stateActiveEdge
					// consts below, for the reason the valence block states: form
					// chrome is not the workflow axis, and re-ruling one axis must
					// not drag another.
					"secondary-ink": "#8f5200", // 6.22:1 - orange as text
					"secondary-edge": "#b3671a", // 4.31:1 - orange as a 2px edge
					// #f09d37 at 0.15 over white. See primary-tint above for
					// the ruling and for why the on-colour is pinned to fg-1.
					// A tint is a FILL: 1.12:1 on white, so it may never go
					// on an edge. The 9 chromatic borders and 1 outline
					// DCTL-2441 swept took border-default or secondary-edge.
					"secondary-tint": "#fdf0e1",
					"on-secondary-tint": "#424242",
					accent: turquoise,
					error: red,
					"on-error": fgOnColor,
					// REV 2: info is BLUE. It rendered amber for years; that
					// was the drift. This is the one intended visual change in
					// this commit.
					info: blue,
					"on-info": fgOnColor,
					// Activated by DCTL-2294, together in ONE commit and with
					// the 12 rework call sites that were reading this key by
					// mistake. Renders Vuetify's #FB8C00 today.
					//
					// The pair is indivisible: getForeground("#FB8C00") is
					// #fff but getForeground("#f9a825") is #000, so declaring
					// the base alone auto-flips the DERIVED on-colour from
					// white to near-black and the two fills below shift twice.
					//
					// on-warning reaches exactly 2 sites, not 45 - measured on
					// this branch, not inherited. Every foreground use (text-*,
					// color= on an icon) and every variant="tonal" consumes the
					// base key and never the on-colour, and all 13 remaining
					// type="warning" alerts are tonal. (A grep returns 16
					// type="warning"; one is a comment in this file and two
					// were the rework banners DCTL-2294 moved off amber.) The 2
					// real fills are both variant="flat":
					//   StepDeduplicate.vue:87 - the "pending" filter button
					//   ConfirmDialog.vue:30-33, reached only by
					//     SourceDataExtraction/index.vue:233 of its 11 callers
					// Both carry a white label on amber today - 2.37:1, a live
					// WCAG 1.4.3 failure - and read 7.22:1 after this.
					warning: "#f9a825",
					"on-warning": "#3d2600",
					// Activated by DCTL-2293, with the reconciliation of the
					// green/purple provenance-QC axis. Renders Vuetify's
					// #4CAF50 today.
					//
					// THE GATE THIS WAITED ON, and why it was a gate rather
					// than a sweep: #4caf50 was double-booked - it meant BOTH
					// "the QC reviewer authored this value" (provenance) and
					// "this is resolved" (a valence). Declaring `success`
					// re-values every green at once, so any site meaning
					// provenance had to be lifted off this key FIRST or it
					// would silently restate provenance as completion. Those
					// were QcSdeDialog's whole QC-Mode banner (chip, button,
					// wash and edge - two of the four were literal greens no
					// `color="success"` grep could see) and three rows of
					// DataPreviewView's cellSource list. All now name
					// provenance-qc. What stays here is what genuinely means
					// resolved / complete / agree / applied.
					//
					// on-success RIDES FREE: getForeground returns #ffffff at
					// both #4CAF50 and #2e7d32, so declaring it changes
					// nothing by itself. Declared anyway so it stops dropping
					// out of the held-entry accounting the way it did before
					// DCTL-2281 - and so lint-on-color can guard it, which is
					// the half that does bite. There are 0 bg-success call
					// sites today, so the guard is forward cover.
					success: "#2e7d32",
					"on-success": fgOnColor,

					// --- Surfaces & lines (opaque; no black-alpha borders) -
					surface: trueWhite,
					"on-surface": "#424242",
					"surface-recessed": "#f4f5f6",
					"surface-hover": "#eceef0",
					"surface-selected": "#e9ebee",
					// The opaque stand-in for a NEUTRAL `variant="tonal"` fill:
					// #424242 at 0.12 over white is exactly #e8e8e8, so on a
					// white card the swap is pixel-identical. It is declared as
					// a theme key rather than written as a hex because that is
					// what makes Vuetify emit the `bg-surface-tint` utility -
					// without the key the class parses and paints nothing.
					// `on-surface-tint` is pinned rather than left to Vuetify.
					// Left undeclared it is DERIVED, and `.bg-surface-tint`
					// carries a `color:` of its own from that derivation -
					// measured as #000000, not the #424242 `currentColor`
					// already resolved to under a tonal chip. Every call site
					// pairs the fill with `text-fg-1`, and the ink class wins
					// the colour both rules set (see constants/workflowStates.js
					// on the `.bg-*` before `.text-*` emission order), so this
					// key changes no rendered pixel today. It is here so the
					// fill is legible on its own terms - 8.20:1 - instead of
					// only in company, which is the same reason the theme pins
					// on-secondary and on-queued. (DCTL-2283)
					"surface-tint": "#e8e8e8",
					"on-surface-tint": "#424242",
					divider: "#e3e6e9",
					"border-default": "#cfd4d9",
					"border-strong": "#8b949c",
					// Text ramp. Assignment is BY ROLE, not by source value
					// (ruled 2026-08-03): headings -> fg-max, body and
					// informational text -> fg-2, caption and metadata ->
					// fg-3. Neither blanket sweep is correct, which is why
					// DCTL-2290 is hand classification and not a codemod:
					//   - grey-darken-1 is EXACTLY fg-3 #757575 and sits on
					//     the 4.61:1 AA floor, so a pixel-preserving blanket
					//     sweep parks ~295 occurrences there and violates the
					//     house rule "no light grey text on white".
					//   - a blanket fg-2 darkens the caption text the design
					//     system explicitly sanctions at #757575.
					// grey-darken-2 #616161 (6.19:1) is NOT fg-2 #5f6368
					// (6.05:1) - close, not equal - so its 118 occurrences
					// are a real small value change too.
					// fg-disabled is reserved for the disabled STATE and is
					// never informational text, so a light grey used as text
					// gets PROMOTED to fg-3 / fg-2 rather than mapped down to
					// fg-disabled, which is itself only 2.59:1.
					// The "11 grey-lighten sites" this comment used to claim
					// was wrong twice over (measured 2026-08-05, DCTL-2285 -
					// which owns them, not DCTL-2291). Of 18 grey-lighten-*
					// occurrences only 5 are the TEXT form, and of those 5
					// only 2 are on a light surface. The other 3 render inside
					// tooltips - StepNormalize.vue:775 and TagPeekContent.vue:3
					// / :25, whose own .peek-tag is rgba(255,255,255,.62) -
					// where light grey is CORRECT and promoting it to fg-2
					// would make the text nearly invisible.
					// A contrast ratio is a property of a PAIR, never of an
					// ink, so no grey can be classified without its backdrop.
					"fg-max": "#212121",
					// fg-1 is a second name for a value the theme already
					// carries - on-surface above is also #424242 - and that
					// is intentional. on-surface is a Vuetify role; fg-1 is a
					// position on the design system's text ramp ($fg1 in
					// scss/_tokens.scss), and a sweep has to be able to name
					// the ramp. Without this key the 13 grey-darken-3 sites
					// had no target at all, which is what blocked DCTL-2291.
					// The scss spelling is undashed ($fg1) and the handoff
					// theme omits the key entirely; both are pre-existing.
					"fg-1": "#424242",
					"fg-2": "#5f6368",
					"fg-3": "#757575",
					"fg-disabled": "#9aa2aa",

					// --- Axis 1 - Provenance (partner: A / B / QC badge) --
					"provenance-a-ink": "#2c657d",
					"provenance-a": "#347b99",
					"provenance-a-tint": "#ebf2f5",
					"provenance-b-ink": "#8f5200",
					"provenance-b": "#f09d37",
					"provenance-b-edge": "#b3671a",
					"provenance-b-tint": "#fef5eb",
					"provenance-qc-ink": "#5c4185",
					"provenance-qc": "#6a4c93",
					"provenance-qc-tint": "#f0edf4",
					"provenance-other-ink": "#5f6368",
					"provenance-other": "#8b949c",
					"provenance-other-tint": "#f3f4f5",

					// --- Axes 3 + 4 - Valence (diff AND feedback) ---------
					// Provenance and valence share some hexes with the state
					// buckets below, but they are independent axes: the design
					// system writes them as literals so re-ruling one axis
					// cannot drag another. Kept literal here for that reason.
					"valence-positive-ink": "#1b5e20",
					"valence-positive": "#2e7d32",
					"valence-positive-tint": "#eaf2eb",
					"valence-negative-ink": "#c62828",
					"valence-negative": "#d32f2f",
					"valence-negative-tint": "#fbeaea",
					"valence-attention-ink": "#8a5000",
					"valence-attention": "#f9a825",
					"valence-attention-edge": "#bd7a00",
					"valence-attention-tint": "#fef6e9",
					"valence-info-ink": "#2c657d",
					"valence-info": "#347b99",
					"valence-info-tint": "#ebf2f5",
					"valence-neutral-ink": "#5f6368",
					"valence-neutral": "#8b949c",
					"valence-neutral-tint": "#f3f4f5",

					// --- Axis 2 - Workflow state (REV 2.1.1) --------------
					// Bucket QUEUED - Available + Assigned + Ready for QC:
					// no work has happened yet
					"state-available-ink": stateQueuedInk,
					"state-available": stateQueued,
					"state-available-tint": stateQueuedTint,
					"on-queued": onQueued,
					"state-assigned-ink": stateQueuedInk, // 2.1.1: was the in-flight orange
					"state-assigned": stateQueued, // claimed but not started is still queued
					"state-assigned-tint": stateQueuedTint,
					"state-ready-ink": stateQueuedInk, // 2.1: was #2c657d
					"state-ready": stateQueued, // 2.1: was #347b99, blue back to provenance
					"state-ready-tint": stateQueuedTint,
					// Bucket IN FLIGHT - In progress + In QC: work is
					// actively happening
					"state-progress-ink": stateActiveInk,
					"state-progress": stateActive,
					"state-progress-edge": stateActiveEdge,
					"state-progress-tint": stateActiveTint,
					"state-in-qc-ink": stateActiveInk, // 2.1: was #5c4185
					"state-in-qc": stateActive, // 2.1: was #6a4c93, plum back to provenance
					"state-in-qc-edge": stateActiveEdge,
					"state-in-qc-tint": stateActiveTint,
					// Bucket REWORK - the only state with its own hue and a
					// 4px left edge
					"state-rework-ink": stateReworkInk,
					"state-rework": stateRework,
					"state-rework-tint": stateReworkTint,
					// Bucket COMPLETE
					"state-complete-ink": stateCompleteInk,
					"state-complete": stateComplete,
					"state-complete-tint": stateCompleteTint,

					// --- Selection / focus - achromatic on purpose --------
					"ring-selected": "#424242",
					"ring-focus": "#212121",

					// --- Deprecated, pending the DCTL-2266 call-site sweeps -
					// The REV 2 handoff omits all four. Each still has live
					// consumers, so dropping one now would be a visual change:
					//   grey      - shadows Vuetify's Material .bg-grey; 44
					//               cancel/dismiss buttons rely on #f5f5f5
					//               plus a dark label, not #9e9e9e plus white.
					//   trueWhite - the defaults block above names it as
					//               bgColor for VCombobox/VAutocomplete/VSelect.
					//   white     - 15 text-white + 28 bg-white uses, counted
					//               in single-line class attributes. Was 125
					//               text-white until DCTL-2282 deleted the 108
					//               sitting on the `secondary` theme key. The
					//               rest are on other fills, and several still
					//               fail AA - but on HARDCODED hex, not on a
					//               theme key, so no on-<key> reaches them.
					//               The worst is now StepMerge.vue:230 and
					//               StepReview.vue:111, filled with the
					//               per-step accent from QualityControl/
					//               constants.js: STEP_MERGE_COLOR #FF7675
					//               under this key's #FAFAFA is 2.48:1. Both
					//               belong to DCTL-2258, which retires the six
					//               per-step hues for a numbered rail.
					//               The example that stood here before -
					//               StepLoadPreview's source chip, then at :56
					//               and since moved to :73 by DCTL-2189 - is
					//               fixed. DCTL-2298 put it on an opaque
					//               provenance tint + ink, so it sets no label
					//               colour at all and reads at 5.68-7.11:1.
					//   secondaryDark - one consumer,
					//               components/VMetadata/VMetadataValueField.vue:27.
					//               Kept under this name rather than renamed to
					//               secondary-darken-1 (declared above as well);
					//               renaming would break that consumer.
					grey: grey,
					white: white,
					trueWhite: trueWhite,
					secondaryDark: darkOrange,

					// --- NEVER DECLARE -----------------------------------
					// on-background is not a held entry and not an activation
					// candidate. It belongs on no list, at no value, ever.
					// It is the BASE INK OF THE WHOLE APPLICATION:
					// VApp.css:4 sets
					//   .v-application { color: rgba(
					//       var(--v-theme-on-background),
					//       var(--v-high-emphasis-opacity)) }
					// and high-emphasis-opacity is 1 (below), so every
					// character the app renders without an explicit colour
					// inherits rgb(on-background). on-background DERIVES to
					// 0,0,0 from background, so that base ink is black today.
					// Declaring it recolours the entire app in one edit, with
					// no call site to review and nothing to sweep.
					// This reason does NOT depend on the class sweeps:
					// .text-high-emphasis / .text-medium-emphasis /
					// .text-disabled read the same key (main.css:7306/7310/
					// 7314), but DCTL-2338 took the app to ZERO
					// text-medium-emphasis call sites and the argument above
					// still stands unchanged.

					// --- HELD: 3 entries - 2 colour keys + 1 variable -----
					// The running total in this comment was stale at 9 and the
					// arithmetic is now spelled out so the next reader can check
					// it rather than inherit it. Counted by grepping the colors
					// block above for each candidate:
					//   10  original REV 2 held list
					//   -1  medium-emphasis-opacity   DCTL-2338
					//   -2  border-color + border-opacity, DCTL-2288 - the
					//       activation this count had never been decremented for
					//   -2  warning + on-warning      DCTL-2294 (declared above)
					//   -2  success + on-success      DCTL-2293 (declared above)
					//   ---
					//    3  background, on-accent, and disabled-opacity
					//       (held but REJECTED, see below)
					// All 3 are in design-system/handoff/vuetify-theme.js and
					// deliberately not declared, because each moves a value
					// the app already renders and DCTL-2252 was contracted to
					// be a visual no-op apart from `info`. Each names the
					// ticket that activates it together with its call-site
					// sweep; do not add any of them piecemeal.
					//
					// THE MECHANIC behind the counts below: an on-<key>
					// reaches a site only where Vuetify emits bg-<key>. Every
					// FOREGROUND use - text-*, color= on an icon, and every
					// variant="tonal" - consumes the base key and never reads
					// the on-colour. That one fact is why on-warning is 2
					// sites and not 45, and on-accent 2 and not 5. DCTL-2282
					// shipped on exactly this reasoning and confirmed it:
					// on-secondary matched 164 patterns and reached 112.
					// Vuetify's own rule is narrower than "tonal" suggests -
					// composables/variant.js emits a background for variant
					// "elevated" and "flat" ONLY, everything else takes a text
					// colour, and VBtn defaults to elevated while VChip
					// defaults to tonal.
					//
					// Colour keys (2 of the 3). warning + on-warning and
					// success + on-success were four of the six and now ship
					// above:
					//   background: "#fafafa"  -> DCTL-2289
					//       Vuetify's default light theme has #FFFFFF, so
					//       .v-application paints white today and this tints
					//       every page.
					//   "on-accent": "#0f6b63"  -> DCTL-2292, which is not
					//       pass-4 work: all three shields are turquoise. 5
					//       references, 2 effective; 2 of the 3 shields are a
					//       text-color="white" prop no class sweep matches.
					//       Derives to black today.
					// surface, on-surface, on-primary and on-error already
					// match the handoff, so those are declared above as
					// no-ops.
				},
				variables: {
					// LIVE, shipped by DCTL-2252. Not held - do not read it as
					// an activation candidate. There are 0 app
					// text-high-emphasis sites; it reaches Vuetify internals
					// only.
					"high-emphasis-opacity": 1,
					// LIVE, shipped by DCTL-2338. Was the one held entry
					// carrying "no ticket; judgment call". THE JUDGMENT, and
					// why it was stuck: the two sides are not the same kind of
					// cost, and the old comment here weighed them as if they
					// were.
					//   COST is aesthetic, and it is never a contrast loss -
					//     every reader of this variable gets DARKER at 1.
					//     .text-medium-emphasis resolves
					//     rgba(var(--v-theme-on-background), <opacity>) and
					//     on-background derives to 0,0,0, so opacity 1 would
					//     have taken 144 class sites from #666666 to pure
					//     black - indistinguishable from .text-high-emphasis,
					//     i.e. the secondary/primary distinction gone. That is
					//     why all 144 moved onto the flat fg-2 #5f6368
					//     (6.05:1) FIRST - 127 SDE sites, then the last 17 in
					//     the same MR as this line. App call sites are now 0.
					//     What the sweep does NOT neutralise, and is accepted:
					//     Vuetify's own readers lose their de-emphasis. List
					//     and card subtitles, list subheaders, field hints and
					//     counters go to full strength; a non-current
					//     v-stepper-item stops being faded (--selected was
					//     already 1); an unchecked v-selection-control glyph
					//     stops being faded (--dirty was already 1). Each keeps
					//     a non-opacity signal - the QC step rail marks the
					//     current step with a background tint plus
					//     font-weight 600 and fades inactive avatars itself
					//     (QualityControl/index.vue:2037-2048), and a checkbox
					//     still differs by glyph and colour.
					//   GAIN is accessibility, measured in a browser on
					//     b7a18ddd, twice (computed-style compositing and
					//     darkest rendered pixel agreed):
					//       resting field label on a Vuetify surface
					//         #8e8e8e 3.29:1 FAIL -> #424242 10.05:1
					//       resting field label on the page background
					//         #666666 5.74:1 -> #000000 21.00:1
					//       .v-messages hint / rule / error, same two contexts
					//       SingletonItemsList.vue:378 (text-info in a
					//         v-list-item-subtitle, $list-item-subtitle-opacity
					//         falls through to here)
					//         #85b0c2 2.34:1 FAIL -> #347b99 4.73:1
					//     The resting label's ratio is a property of its
					//     CONTAINER, not of this variable alone: VLabel.css:8
					//     is `color: inherit` + this opacity, so the 0.6 scaled
					//     whatever ink was inherited - on-surface #424242
					//     inside a card/panel/dialog, black on plain
					//     background. Two ratios, one mechanism.
					//   NOT REPRODUCIBLE, do not reinstate: an outlined-notch
					//     label at 1.48:1. $field-outline-opacity .38 is
					//     applied as opacity on __start / __notch::before /
					//     __notch::after / __end (VField.css:409-411) -
					//     pseudo-element borders - so it does not dim a label
					//     nested in __notch. Measured opacity chain there is
					//     0.600, same as every other variant. Nor is there any
					//     0.6 x 0.6 compounding: $field-color
					//     (VField/_variables.scss:9) is a SASS !default hook
					//     consumed NOWHERE in the compiled library.
					// A sweep alone could never have retired this variable:
					// 25 compiled CSS rules across 20 Vuetify stylesheets read
					// it. Only 10 component .sass files name it directly; the
					// other 15 readers come through a _variables.scss
					// indirection ($label-opacity, $card-subtitle-opacity,
					// $list-item-subtitle-opacity, $messages-color,
					// $counter-color, $text-field-affix-color, ...), which is
					// why counting source files that contain the string
					// undercounts the readers and misses VLabel - the failing
					// surface - entirely.
					"medium-emphasis-opacity": 1,
					// LIVE, shipped by DCTL-2288 together with the 136 literal
					// black-alpha border call sites, deliberately in ONE commit:
					// landing this pair earlier gives two-tone borders app-wide,
					// landing it later leaves the literals inconsistent for a
					// release. This is the ONLY mechanism that reaches the
					// IMPLICIT borders - a variant="outlined" call site writes no
					// colour at all, so no sweep can find it.
					//   Measured on this branch, not inherited from the ticket
					//   (which said ~213 = 179 literal + ~34 bound):
					//     180  literal variant="outlined" across 74 files
					//      51  bound :variant= that can resolve to outlined -
					//          34 name it on their own line, plus 17 of the 26
					//          multi-line ternaries. The other 21 bound sites are
					//          flat/tonal/text pairs that never reach outlined.
					//    ---
					//     231 implicit borders lifted by these two lines.
					//   Contrast goes UP, not down: rgba(0,0,0,.12) composites to
					//   ~#e0e0e0 (1.32:1 on white); #cfd4d9 is 1.49:1. Opaque, so
					//   it also stops compositing differently per surface - the
					//   reason COLOR.md rule 5 bans alpha borders outright.
					"border-color": "#cfd4d9",
					"border-opacity": 1,
					// REJECTED - the last of the 9, not scheduled.
					//   "disabled-opacity": 1  -> REJECTED, not scheduled.
					//       .text-disabled has ZERO app call sites, so the
					//       variable is read only by Vuetify's disabled-state
					//       selectors: setting it to 1 makes disabled controls
					//       visually identical to enabled ones app-wide. WCAG
					//       1.4.1 / 3.2 regression with no sweepable upside.
					//       Put fg-disabled on the element instead.
				},
			},
		},
	},
});
